import { publicListPieceHref } from "@/lib/public-list/public-list-href";
import type { CatalogScope, ListedPiece, PieceLinks } from "./catalog-types";
import { hostTabHref } from "./host-tabs";
import type { RankingItem } from "./host-panel-types";
import { CATALOG_MODE_PARAM, catalogScopeToSearchParams } from "./panel-query";

/** Minha Lista, the tab a piece of the host's own list is read from and returned to. */
const HOST_LIST_TAB_HREF = hostTabHref("lista");

/**
 * Where a piece's name and thumbnail lead: its detail route.
 *
 * The prototype links every ranked piece to the product page. The route is keyed by the backend
 * `Product.id`, the one key the panel has for a piece; it is one function so that, the day the
 * page is keyed by slug, the panel changes in one place.
 *
 * This is the **fixture's** address: the reference scenarios carry no real list item, so their
 * pieces have no page of their own and land on `/produtos/[id]`. A real list uses
 * `hostPieceHref` below.
 */
export function pieceDetailHref(pieceId: string, scope: CatalogScope = "dentro"): string {
  const href = `/produtos/${encodeURIComponent(pieceId)}?contexto=${encodeURIComponent(HOST_PDP_CONTEXT)}`;

  return withCatalogScope(href, scope);
}

/**
 * The origin the PDP reads (`derivePdpContext`): anything starting with "Anfitri" opens it in
 * `gestao` — the host's own view of the piece, with "Minha lista" in the breadcrumb and the
 * arrangement it was put on the list with. Never shown on screen; it only steers the page.
 */
export const HOST_PDP_CONTEXT = "Anfitrião · minha lista";

/**
 * A piece of the host's own list, on the page a guest sees — in the host's context.
 *
 * The PDP is keyed by the **list item**, not by the product (`/lista/<slug>/produto/<itemId>`),
 * so only a piece that is on the list has one. Escopo_Fora's pieces, and any list the panel
 * could not name, get `null`: no link at all, rather than a link to an empty page.
 *
 * `scope` is the panel's own `modo` param, carried along so the PDP can hand the address back:
 * the back arrow used to take the host to the **guest's** list page, which is not where they
 * were and, on a draft, does not even exist (reported 2026-10-07). Carried as a parameter and
 * not as a full return URL on purpose — a URL in a query string is an open redirect waiting to
 * be found, and the only thing that varies here is which scope the tab reopens in.
 */
export function hostPieceHref(
  slug: string,
  listItemId: string | undefined,
  scope: CatalogScope = "dentro",
): string | null {
  if (slug === "" || listItemId === undefined) return null;

  // `encodeURIComponent`, not `URLSearchParams`: the latter writes a space as `+`, which decodes
  // the same but would rewrite every address the panel has ever handed out, for nothing.
  const href = `${publicListPieceHref(slug, listItemId)}?contexto=${encodeURIComponent(HOST_PDP_CONTEXT)}`;

  return withCatalogScope(href, scope);
}

/**
 * Where the PDP's back arrow returns a host to: the tab they came from, in the scope they
 * were in (`modo=add` for Escopo_Fora).
 */
export function hostListReturnHref(scope: CatalogScope): string {
  const params = catalogScopeToSearchParams(scope).toString();

  return params === "" ? HOST_LIST_TAB_HREF : `${HOST_LIST_TAB_HREF}?${params}`;
}

/**
 * Restates an address's scope.
 *
 * The grid's links are built on the server, with the scope the request carried; the floating
 * control then changes scope **on the client**, by rewriting the query string without a server
 * round trip (`replacePanelQuery`). So the link a card hands out has to be restated against the
 * scope the person is actually looking at, or the back arrow returns them to the other one.
 */
export function withCatalogScope(href: string, scope: CatalogScope): string {
  const [path, query = ""] = href.split("?");

  /*
   * The other parameters are carried **verbatim**, never re-encoded: a round trip through
   * `URLSearchParams` would turn every `%20` into `+`, which decodes the same and changes every
   * address for nothing.
   */
  const kept = query
    .split("&")
    .filter((pair) => pair !== "" && pair.split("=")[0] !== CATALOG_MODE_PARAM);

  const next = [...kept, ...catalogScopeToSearchParams(scope).toString().split("&")]
    .filter((pair) => pair !== "")
    .join("&");

  return next === "" ? path : `${path}?${next}`;
}

/**
 * The grid's targets as data, which is what a Server Component can hand a client one.
 *
 * **Every piece gets a page**, by one of the two addresses a piece can have:
 *
 * - on the list → `/lista/{slug}/produto/{itemId}`, the page a guest opens;
 * - not on the list → `/produtos/{productId}`, the catalog's own page.
 *
 * Until 2026-10-07 only the first existed, so a piece in Escopo_Fora that the host had not added
 * fell out of the map and the card lost its link, its hover and its click — exactly when the
 * person was deciding whether to want it. The second address is the one that fixes that.
 */
export function pieceHrefsFor(
  links: PieceLinks | null,
  pieces: readonly ListedPiece[],
): Record<string, string> {
  const hrefs: Record<string, string> = {};

  for (const piece of pieces) {
    /*
     * A list the panel could not read has no slug, so nothing on it can be addressed as a list
     * item — but each piece is still a catalog product, and that page opens all the same.
     */
    const asListItem =
      links === null ? null : hostPieceHref(links.slug, links.listItemIdByProductId[piece.id]);

    hrefs[piece.id] = asListItem ?? pieceDetailHref(piece.id);
  }

  return hrefs;
}

/**
 * The join between a list's items and the products the panel names them by.
 *
 * Structural on purpose: it takes the item shape both the catalog and the panel already hold,
 * so neither has to reach for the other's adapter.
 */
export function pieceLinksFrom(
  slug: string,
  items: readonly { id: string; product: { id: string } }[],
): PieceLinks {
  const listItemIdByProductId: Record<string, string> = {};

  for (const item of items) {
    listItemIdByProductId[item.product.id] = item.id;
  }

  return { slug, listItemIdByProductId };
}

/**
 * The ranking's three pieces, each pointing at its own page.
 *
 * A piece that is no longer on the list — gifted, then removed — keeps the address it had:
 * losing the link would be a worse answer than the page the prototype points at.
 */
export function rankingWithPieceHrefs(
  ranking: readonly RankingItem[],
  links: PieceLinks,
): RankingItem[] {
  return ranking.map((item) => ({
    ...item,
    href: hostPieceHref(links.slug, links.listItemIdByProductId[item.pieceId]) ?? item.href,
  }));
}
